import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Link2,
  Tag,
  Key,
  CreditCard,
  Users,
  Globe,
  Receipt,
  LogOut,
  Settings,
  ChevronRight,
  Zap,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CliqoMark } from "@/components/marketing/Brand";
import Kbd from "@/components/ui/Kbd";
import { useAuth } from "@/hooks/useAuth";
import { useCommands } from "@/context/CommandsContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/lib/ui/sheet";
import { Avatar, AvatarFallback } from "@/lib/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/lib/ui/dropdown-menu";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: React.ReactNode;
  end?: boolean;
  /** Second key of the "G then …" navigation shortcut (user nav only). */
  shortcut?: string;
}

type SidebarVariant = "user" | "admin";

export interface SidebarProps {
  variant?: SidebarVariant;
  isOpen?: boolean;
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Nav configuration
// ---------------------------------------------------------------------------

const USER_NAV_ITEMS: NavItem[] = [
  {
    label: "Dashboard",
    href: "/app/dashboard",
    icon: LayoutDashboard,
    end: true,
    shortcut: "D",
  },
  {
    label: "Links",
    href: "/app/links",
    icon: Link2,
    shortcut: "L",
  },
  {
    label: "Tags",
    href: "/app/tags",
    icon: Tag,
    shortcut: "T",
  },
  {
    label: "Domains",
    href: "/app/domains",
    icon: Globe,
    shortcut: "M",
  },
  {
    label: "API Keys",
    href: "/app/api-keys",
    icon: Key,
    shortcut: "K",
  },
  {
    label: "Billing",
    href: "/app/billing",
    icon: CreditCard,
    shortcut: "B",
  },
];

const ADMIN_NAV_ITEMS: NavItem[] = [
  {
    label: "Dashboard",
    href: "/admin/dashboard",
    icon: LayoutDashboard,
    end: true,
  },
  {
    label: "Users",
    href: "/admin/users",
    icon: Users,
  },
  {
    label: "Links",
    href: "/admin/links",
    icon: Link2,
  },
  {
    label: "Payments",
    href: "/admin/payments",
    icon: Receipt,
  },
  {
    label: "Billing Plans",
    href: "/admin/billing-plans",
    icon: CreditCard,
  },
  {
    label: "Domains",
    href: "/admin/domains",
    icon: Globe,
  },
];

// ---------------------------------------------------------------------------
// Nav link items
// ---------------------------------------------------------------------------

interface NavLinkItemProps {
  item: NavItem;
  isAdmin?: boolean;
}

const NavLinkItem: React.FC<NavLinkItemProps> = ({ item, isAdmin = false }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
          isAdmin
            ? isActive
              ? "bg-card/15 text-white"
              : "text-red-200 hover:bg-card/10 hover:text-white"
            : isActive
              ? "bg-brand/10 font-semibold text-brand"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Flame rail on the active user item */}
          {!isAdmin && isActive && (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand"
            />
          )}
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && <span>{item.badge}</span>}
          {!isAdmin && item.shortcut && (
            <span className="hidden items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 lg:flex">
              <Kbd>G</Kbd>
              <Kbd>{item.shortcut}</Kbd>
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

// ---------------------------------------------------------------------------
// Link credit balance card
// ---------------------------------------------------------------------------

const CreditBalanceCard: React.FC = () => {
  const { user } = useAuth();
  const credits = user?.userBalance?.links ?? 0;

  return (
    <NavLink
      to="/app/billing"
      className="group mb-2 block rounded-xl border border-border bg-muted/40 px-3 py-2.5 transition-colors hover:bg-muted"
      aria-label={`${credits} link credits remaining — go to billing`}
    >
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
          <Zap className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] font-medium uppercase leading-none tracking-[0.18em] text-muted-foreground">
            Link credits
          </p>
          <p className="font-display text-lg font-extrabold leading-tight tabular-nums text-foreground">
            {credits.toLocaleString()}
          </p>
        </div>
        <span className="shrink-0 text-[11px] font-semibold text-brand opacity-0 transition-opacity group-hover:opacity-100">
          Top up
        </span>
      </div>
    </NavLink>
  );
};

// ---------------------------------------------------------------------------
// User footer with DropdownMenu
// ---------------------------------------------------------------------------

interface UserFooterProps {
  isAdmin?: boolean;
  onLogout: () => void;
}

const UserFooter: React.FC<UserFooterProps> = ({
  isAdmin = false,
  onLogout,
}) => {
  const { user } = useAuth();

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() ||
      user.email?.[0]?.toUpperCase() ||
      "U"
    : "U";

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email ?? "User");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
            isAdmin
              ? "hover:bg-card/10 text-white"
              : "hover:bg-accent text-foreground",
          )}
          aria-label="Open user menu"
        >
          <Avatar className="w-8 h-8 shrink-0">
            <AvatarFallback
              className={cn(
                "text-xs font-semibold",
                isAdmin
                  ? "bg-card/20 text-white"
                  : "bg-brand/15 text-brand",
              )}
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                "text-sm font-medium truncate",
                isAdmin ? "text-white" : "text-foreground",
              )}
            >
              {displayName}
            </p>
            <p
              className={cn(
                "text-xs truncate",
                isAdmin ? "text-red-300" : "text-muted-foreground",
              )}
            >
              {user?.email}
            </p>
          </div>
          <ChevronRight
            className={cn(
              "w-4 h-4 shrink-0",
              isAdmin ? "text-red-300" : "text-muted-foreground",
            )}
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" className="w-56 mb-1">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-foreground">{displayName}</span>
            <span className="text-xs text-muted-foreground font-normal">
              {user?.email}
            </span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <a
            href="/app/profile"
            className="flex items-center gap-2 cursor-pointer"
          >
            <Settings className="w-4 h-4" aria-hidden="true" />
            Profile &amp; Settings
          </a>
        </DropdownMenuItem>

        {!isAdmin && (
          <DropdownMenuItem asChild>
            <a
              href="/app/billing"
              className="flex items-center gap-2 cursor-pointer"
            >
              <CreditCard className="w-4 h-4" aria-hidden="true" />
              Billing
            </a>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={onLogout}
          className="flex items-center gap-2 text-red-600 dark:text-red-300 focus:text-red-600 dark:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/30 dark:bg-red-900/30 cursor-pointer"
        >
          <LogOut className="w-4 h-4" aria-hidden="true" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------------------------------------------------------------------
// Inner panel (shared between desktop and Sheet)
// ---------------------------------------------------------------------------

interface SidePanelProps {
  isAdmin: boolean;
  navItems: NavItem[];
  onLogout: () => void;
}

const SidePanel: React.FC<SidePanelProps> = ({
  isAdmin,
  navItems,
  onLogout,
}) => {
  const { openCreateLink } = useCommands();

  return (
  <div
    className={cn(
      "flex flex-col h-full w-full",
      isAdmin ? "bg-red-900" : "bg-card border-r border-border",
    )}
  >
    {/* ── Logo ────────────────────────────────────────────────────────── */}
    <div
      className={cn(
        "flex items-center gap-2.5 px-5 h-16 shrink-0",
        isAdmin ? "border-b border-red-800" : "border-b border-border",
      )}
    >
      <span
        className={cn(
          "inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0",
          isAdmin
            ? "bg-card/10 text-white"
            : "bg-primary text-primary-foreground",
        )}
        aria-hidden="true"
      >
        <CliqoMark className="w-5 h-5" />
      </span>

      <div className="flex-1 min-w-0">
        <span
          className={cn(
            "font-brand text-base font-extrabold tracking-tight",
            isAdmin ? "text-white" : "text-foreground",
          )}
        >
          cliqo
        </span>
        {isAdmin && (
          <span className="ml-2 inline-flex items-center rounded-md bg-red-800 px-1.5 py-0.5 text-[10px] font-semibold text-red-200 ring-1 ring-inset ring-red-700/50">
            Admin
          </span>
        )}
      </div>
    </div>

    {/* ── Navigation ──────────────────────────────────────────────────── */}
    <nav
      className="flex-1 overflow-y-auto px-3 py-4"
      aria-label={isAdmin ? "Admin menu" : "Main menu"}
    >
      {/* Primary action — create a link */}
      {!isAdmin && (
        <button
          type="button"
          onClick={openCreateLink}
          className="group mb-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-12px_hsl(var(--flame)/0.9)] active:translate-y-0"
        >
          <Plus
            className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90"
            aria-hidden="true"
          />
          New link
          <Kbd className="ml-1 hidden border-primary-foreground/30 bg-transparent text-primary-foreground/80 sm:inline-flex">
            N
          </Kbd>
        </button>
      )}

      <p
        className={cn(
          "mb-1.5 px-3 font-mono text-[10px] font-medium uppercase tracking-[0.18em]",
          isAdmin ? "text-red-400" : "text-muted-foreground",
        )}
      >
        {isAdmin ? "Management" : "Navigation"}
      </p>

      <ul className="space-y-0.5" role="list">
        {navItems.map((item) => (
          <li key={item.href} role="listitem">
            <NavLinkItem item={item} isAdmin={isAdmin} />
          </li>
        ))}
      </ul>

      {!isAdmin && (
        <div className="mt-6">
          <p className="mb-1.5 px-3 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Account
          </p>
          <ul className="space-y-0.5" role="list">
            <li role="listitem">
              <NavLinkItem
                item={{
                  label: "Profile",
                  href: "/app/profile",
                  icon: Settings,
                }}
              />
            </li>
          </ul>
        </div>
      )}
    </nav>

    {/* ── Footer ──────────────────────────────────────────────────────── */}
    <div className="px-3 pb-4 shrink-0">
      {!isAdmin && <CreditBalanceCard />}
      <UserFooter isAdmin={isAdmin} onLogout={onLogout} />
    </div>
  </div>
  );
};

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

const Sidebar: React.FC<SidebarProps> = ({
  variant = "user",
  isOpen = false,
  onClose,
}) => {
  const { logout } = useAuth();
  const location = useLocation();
  const isAdmin = variant === "admin";
  const navItems = isAdmin ? ADMIN_NAV_ITEMS : USER_NAV_ITEMS;

  // Close mobile drawer on navigation
  React.useEffect(() => {
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* ── Desktop sidebar — always visible ────────────────────────────── */}
      <div className="hidden lg:flex lg:w-64 lg:shrink-0 lg:flex-col">
        <SidePanel isAdmin={isAdmin} navItems={navItems} onLogout={logout} />
      </div>

      {/* ── Mobile sidebar — Sheet drawer ───────────────────────────────── */}
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
        <SheetContent
          side="left"
          className="p-0 w-64"
          aria-label={isAdmin ? "Admin navigation" : "App navigation"}
        >
          {/* Visually hidden title for screen readers */}
          <SheetHeader className="sr-only">
            <SheetTitle>
              {isAdmin ? "Admin Navigation" : "App Navigation"}
            </SheetTitle>
          </SheetHeader>

          <SidePanel isAdmin={isAdmin} navItems={navItems} onLogout={logout} />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Sidebar;
